import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import {
  getWatchReference,
  mergeReferenceDuplicates,
} from '../../services/firebase';
import type { WatchReference } from '../../types';
import type {
  DuplicateCluster,
  MergeReferenceDuplicatesResult,
  VariantOverrideRequiredDetails,
} from '../../types/referenceDedup';

/**
 * ClusterReviewDialog — Phase 2 per-cluster N-way merge.
 *
 * Forked from `PendingApprovals/MergeDialog.tsx` but generalised from a 2-doc
 * diff to an N-doc diff (one column per doc in the cluster). The admin picks a
 * canonical doc (radio per column), optionally overrides individual fields
 * (radio per differing field), reviews the downstream-pointer impact, can dry
 * run, and finally merges.
 *
 * ⚠️ The Merge / Dry run actions call the `mergeReferenceDuplicates` Cloud
 * Function, which is shipped by a parallel watchlock task. Until it (and the
 * `referenceMergeTombstones` Firestore rule) is deployed the actions error out.
 * Deploy: `firebase deploy --only functions:mergeReferenceDuplicates,firestore:rules`.
 *
 * See LEGACY_REF_DEDUP_PLAN.md §2 (merge strategy) and §5 (UI).
 */

interface ClusterReviewDialogProps {
  cluster: DuplicateCluster;
  open: boolean;
  onClose: () => void;
  onMergeComplete: (result: MergeReferenceDuplicatesResult) => void;
}

/** A single field in the diff, addressed by its dot-path on the doc. */
interface FieldDef {
  /** Dot-path key — also what we send in `fieldOverrides`. */
  path: string;
  label: string;
}

interface FieldGroup {
  title: string;
  fields: FieldDef[];
}

/**
 * Field groups shown in the diff. Dot-paths match what the backend expects in
 * `fieldOverrides` (nested objects flattened: `case.material`, etc.).
 *
 * ⚠️ This is a representative — not exhaustive — slice of the (large)
 * WatchReference shape, chosen to surface the fields that actually diverge
 * between legacy/clean duplicates (see the IWC Mark XX example in the plan).
 * `images` is special-cased to a count, not a per-field value. The Movement &
 * calibre group is expanded to the full calibre-completeness slice of
 * MovementInfo (James' request) so a reviewer can tell which doc carries the
 * richer movement data. Open question for James: want the same depth for
 * complications/electronics? (see final message).
 */
const FIELD_GROUPS: FieldGroup[] = [
  {
    title: 'Identity',
    fields: [
      { path: 'reference', label: 'reference' },
      { path: 'referenceNumber', label: 'referenceNumber' },
      { path: 'referenceNormalized', label: 'referenceNormalized' },
      { path: 'model', label: 'model' },
      { path: 'collection', label: 'collection' },
      { path: 'productName', label: 'productName' },
    ],
  },
  {
    title: 'Case',
    fields: [
      { path: 'case.material', label: 'material' },
      { path: 'case.sizeMm', label: 'sizeMm' },
      { path: 'case.diameterMm', label: 'diameterMm' },
      { path: 'case.waterResistance', label: 'waterResistance' },
      { path: 'case.shape', label: 'shape' },
      { path: 'case.finish', label: 'finish' },
    ],
  },
  {
    title: 'Dial & hands',
    fields: [
      { path: 'dial', label: 'dial (top-level)' },
      { path: 'dialAndHands.color', label: 'color' },
      { path: 'dialAndHands.material', label: 'material' },
      { path: 'dialAndHands.finish', label: 'finish' },
      { path: 'dialAndHands.indexes', label: 'indexes' },
      { path: 'dialAndHands.hands', label: 'hands' },
    ],
  },
  {
    title: 'Strap',
    fields: [
      { path: 'strap.material', label: 'material' },
      { path: 'strap.color', label: 'color' },
      { path: 'strap.buckleType', label: 'buckleType' },
    ],
  },
  {
    title: 'Bezel',
    fields: [
      { path: 'bezel', label: 'bezel (material)' },
      { path: 'bezelType', label: 'bezelType' },
    ],
  },
  {
    // Calibre data lives in two places on a WatchReference: the free-text
    // top-level `calibre` string and the structured `movement` (MovementInfo)
    // object. There is NO denormalized calibre-lookup link on the reference
    // (calibreId / calibreName / calibreSource live in the separate calibres
    // lookup collection, not here) — so "is the calibre data complete?" is
    // judged from `calibre` + the movement identity/spec fields below.
    title: 'Movement & calibre',
    fields: [
      { path: 'calibre', label: 'calibre (top-level)' },
      { path: 'movementType', label: 'movementType' },
      { path: 'movement.type', label: 'movement.type' },
      { path: 'movement.baseMovement', label: 'baseMovement' },
      { path: 'movement.frequencyVph', label: 'frequencyVph' },
      { path: 'movement.jewels', label: 'jewels' },
      { path: 'movement.powerReserveHours', label: 'powerReserveHours' },
      { path: 'movement.diameterMm', label: 'diameterMm' },
      { path: 'movement.heightMm', label: 'heightMm' },
      { path: 'movement.isInHouse', label: 'isInHouse' },
      { path: 'movement.countryOfManufacture', label: 'countryOfManufacture' },
      { path: 'movement.isChronometer', label: 'isChronometer' },
      { path: 'movement.isCosc', label: 'isCosc' },
    ],
  },
  {
    title: 'Complications',
    fields: [
      { path: 'functions', label: 'functions' },
      { path: 'features', label: 'features' },
    ],
  },
  {
    title: 'Images',
    fields: [{ path: 'images', label: 'images (count)' }],
  },
];

/** Read a dot-path value off a doc (e.g. `case.material`). */
function getPath(doc: Record<string, unknown> | null, path: string): unknown {
  if (!doc) return undefined;
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, doc);
}

/** True for values we treat as "empty" (no meaningful data). */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/** Human-readable cell value. `images` collapses to its length. */
function formatValue(path: string, value: unknown): string {
  if (path === 'images') {
    return Array.isArray(value) ? String(value.length) : '0';
  }
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  if (value === '') return '—';
  return String(value);
}

/** Canonical comparison key for "do these docs agree on this field?". */
function compareKey(path: string, value: unknown): string {
  if (path === 'images') {
    return Array.isArray(value) ? String(value.length) : '0';
  }
  if (isEmpty(value)) return '';
  return JSON.stringify(value);
}

/**
 * Mirror of the backend smart-merge field rule (watchlock
 * `functions/src/referenceDedup.ts`): for a single diverging field, which doc's
 * value would the merge actually write if the admin sends NO override?
 *
 *  - canonical non-empty            → canonical wins.
 *  - canonical empty + a dup non-empty → first non-empty doc in display order.
 *  - all docs empty                 → canonical (nothing to fill).
 *
 * `values` MUST be in display order (cluster.docs order) so "first non-empty
 * duplicate" matches what the user sees top-to-bottom / left-to-right.
 *
 * This is DISPLAY-ONLY: it sets the pre-selected radio so the UI reflects the
 * merge outcome. It does NOT emit a fieldOverride — overrides still fire only
 * when the admin actively changes a radio (see `fieldOverrides` below).
 */
function smartMergeSource(
  values: { id: string; value: unknown }[],
  canonicalId: string,
): string {
  const canonical = values.find((v) => v.id === canonicalId);
  if (canonical && !isEmpty(canonical.value)) return canonicalId;
  // Canonical empty — backend fills from the first non-empty doc in order.
  const firstNonEmpty = values.find((v) => !isEmpty(v.value));
  return firstNonEmpty ? firstNonEmpty.id : canonicalId;
}

export function ClusterReviewDialog({
  cluster,
  open,
  onClose,
  onMergeComplete,
}: ClusterReviewDialogProps) {
  const isVariant = cluster.confidence === 'variant_excluded';

  // Highest-scored doc — the default canonical for every cluster, including
  // variant_excluded ones (which carry no `recommendedCanonicalId`).
  const highestScoredId = useMemo(() => {
    let best = cluster.docs[0];
    for (const d of cluster.docs) if (d.score > best.score) best = d;
    return best?.referenceId ?? '';
  }, [cluster.docs]);

  const defaultCanonicalId = cluster.recommendedCanonicalId ?? highestScoredId;

  const [canonicalId, setCanonicalId] = useState(defaultCanonicalId);
  // Per-field explicit overrides: dot-path → referenceId whose value to use.
  // Absent entries default to the canonical (and to the backend merge rules).
  const [fieldPicks, setFieldPicks] = useState<Record<string, string>>({});
  const [overrideReason, setOverrideReason] = useState('');
  // Set when the callable throws `variant_override_required` for a cluster we
  // didn't pre-classify as a variant — reveals the reason textarea.
  const [overrideRequired, setOverrideRequired] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const [docs, setDocs] = useState<Record<string, WatchReference | null>>({});
  const [loadingDocs, setLoadingDocs] = useState(true);

  const [dryRunResult, setDryRunResult] =
    useState<MergeReferenceDuplicatesResult | null>(null);
  const [running, setRunning] = useState<false | 'dry' | 'merge'>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset per-cluster state whenever the cluster (or open) changes.
  useEffect(() => {
    setCanonicalId(defaultCanonicalId);
    setFieldPicks({});
    setOverrideReason('');
    setOverrideRequired(false);
    setOverrideError(null);
    setDryRunResult(null);
    setRunning(false);
    setConfirmOpen(false);
  }, [cluster.brandId, cluster.identityKey, open, defaultCanonicalId]);

  // Load the full docs for the cluster (the scan only returns a slim signature).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingDocs(true);
    (async () => {
      const entries = await Promise.all(
        cluster.docs.map(async (d) => {
          try {
            const ref = await getWatchReference(cluster.brandId, d.referenceId);
            return [d.referenceId, ref] as const;
          } catch (err) {
            console.error('Failed to load reference', d.referenceId, err);
            return [d.referenceId, null] as const;
          }
        }),
      );
      if (cancelled) return;
      setDocs(Object.fromEntries(entries));
      setLoadingDocs(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, cluster.brandId, cluster.docs]);

  const docIds = cluster.docs.map((d) => d.referenceId);
  const duplicateIds = docIds.filter((id) => id !== canonicalId);

  const needsReason = isVariant || overrideRequired;
  const reasonOk = !needsReason || overrideReason.trim().length >= 10;
  const canMerge = Boolean(canonicalId) && reasonOk && running === false;

  // Build the flat `fieldOverrides` map: only the fields the admin explicitly
  // routed away from the canonical doc. Everything else defers to the backend
  // merge rules (canonical wins, empty canonical slots filled from duplicates).
  const fieldOverrides = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const group of FIELD_GROUPS) {
      for (const field of group.fields) {
        if (field.path === 'images') continue; // images are union-merged server-side
        const source = fieldPicks[field.path];
        if (source && source !== canonicalId) {
          out[field.path] = getPath(
            docs[source] as Record<string, unknown> | null,
            field.path,
          );
        }
      }
    }
    return out;
  }, [fieldPicks, canonicalId, docs]);

  const pointerTotal =
    cluster.pointerCounts.bookmarks +
    cluster.pointerCounts.wishlists +
    cluster.pointerCounts.curatedCollections +
    cluster.pointerCounts.submissionsIndex;

  const setFieldPick = (path: string, referenceId: string) => {
    setFieldPicks((prev) => ({ ...prev, [path]: referenceId }));
  };

  const buildOptions = (dryRun: boolean) => ({
    brandId: cluster.brandId,
    canonicalReferenceId: canonicalId,
    duplicateReferenceIds: duplicateIds,
    fieldOverrides:
      Object.keys(fieldOverrides).length > 0 ? fieldOverrides : undefined,
    variantOverrideReason: needsReason
      ? overrideReason.trim()
      : undefined,
    dryRun,
  });

  /** Catch the `variant_override_required` HttpsError specifically. */
  const handleMergeError = (err: unknown): boolean => {
    const code = (err as { code?: unknown }).code;
    const details = (err as { details?: VariantOverrideRequiredDetails })
      .details;
    if (details?.code === 'variant_override_required') {
      setOverrideRequired(true);
      setOverrideError(
        `This cluster requires a variant-override reason${
          details.conflictField ? ` (conflict on “${details.conflictField}”)` : ''
        }. Explain why these are a data error, not real variants.`,
      );
      toast.error('Variant override reason required.');
      return true;
    }
    const msg =
      typeof code === 'string' && code
        ? `${code}`
        : err instanceof Error
          ? err.message
          : String(err);
    toast.error(`Merge failed: ${msg}`);
    return false;
  };

  const handleDryRun = async () => {
    setRunning('dry');
    setOverrideError(null);
    try {
      const result = await mergeReferenceDuplicates(buildOptions(true));
      setDryRunResult(result);
      toast.success('Dry run complete.');
    } catch (err) {
      console.error('Reference merge dry run failed', err);
      handleMergeError(err);
    } finally {
      setRunning(false);
    }
  };

  const handleMergeConfirmed = async () => {
    setConfirmOpen(false);
    setRunning('merge');
    setOverrideError(null);
    try {
      const result = await mergeReferenceDuplicates(buildOptions(false));
      toast.success(
        `Merged — ${result.duplicatesDeleted} duplicate${
          result.duplicatesDeleted === 1 ? '' : 's'
        } deleted.`,
      );
      onMergeComplete(result);
      onClose();
    } catch (err) {
      console.error('Reference merge failed', err);
      handleMergeError(err);
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={running ? () => {} : onClose}
      title={`Merge cluster — ${cluster.brandId} / ${cluster.identityKey}`}
      size="full"
    >
      {loadingDocs ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Variant warning banner */}
          {isVariant && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-900">
                ⚠ Excluded as a potential variant
              </h4>
              <p className="mt-1 text-sm text-amber-800">
                This cluster was excluded from automatic dedup because{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
                  {cluster.variantExclusionReason ?? 'a variant field'}
                </code>{' '}
                differs across docs. If these are <strong>not</strong> real
                variants but a data error, you can merge them anyway — but you
                must explain why.
              </p>
            </div>
          )}

          {/* Canonical pick + per-field diff table */}
          <div>
            <h4 className="mb-1 font-medium text-gray-900">
              Canonical &amp; field selection
            </h4>
            <p className="mb-3 text-sm text-gray-500">
              Pick the canonical doc (radio in each column header). Diverging
              fields (highlighted) default to the value the merge would actually
              write: the canonical&apos;s value, or — when canonical is empty —
              the first non-empty duplicate (marked
              <span className="italic"> → filled from …</span>). Override any
              field by selecting another doc&apos;s value. Fields all docs agree
              on carry no radio.
            </p>
            <DiffTable
              cluster={cluster}
              docs={docs}
              canonicalId={canonicalId}
              onCanonical={setCanonicalId}
              fieldPicks={fieldPicks}
              onFieldPick={setFieldPick}
            />
          </div>

          {/* Variant override reason */}
          {needsReason && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Override reason{' '}
                <span className="text-gray-400">
                  (required to merge — min 10 chars)
                </span>
              </label>
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={3}
                placeholder="e.g. These are not real variants — the legacy doc baked the dial color into `model` and never set dialAndHands.color. Same physical reference."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {overrideReason.trim().length}/10
                </span>
                {overrideError && (
                  <span className="text-xs text-red-600">{overrideError}</span>
                )}
              </div>
            </div>
          )}

          {/* Downstream impact summary */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="mb-1 text-sm font-medium text-gray-900">
              Downstream impact
            </h4>
            <p className="text-sm text-gray-700">
              Deleting{' '}
              <span className="font-semibold">{duplicateIds.length}</span>{' '}
              duplicate doc{duplicateIds.length === 1 ? '' : 's'} and rewriting{' '}
              <span className="font-semibold">{pointerTotal}</span> downstream
              pointer{pointerTotal === 1 ? '' : 's'}:{' '}
              {cluster.pointerCounts.bookmarks} bookmark
              {cluster.pointerCounts.bookmarks === 1 ? '' : 's'},{' '}
              {cluster.pointerCounts.wishlists} wishlist
              {cluster.pointerCounts.wishlists === 1 ? '' : 's'},{' '}
              {cluster.pointerCounts.curatedCollections} curated collection
              {cluster.pointerCounts.curatedCollections === 1 ? '' : 's'},{' '}
              {cluster.pointerCounts.submissionsIndex} submissions-index entr
              {cluster.pointerCounts.submissionsIndex === 1 ? 'y' : 'ies'}.
            </p>
          </div>

          {/* Dry-run result */}
          {dryRunResult && (
            <DryRunSummary result={dryRunResult} />
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t pt-6">
            <div className="text-sm text-gray-500">
              Canonical:{' '}
              <span className="font-mono text-gray-900">
                {canonicalId || '—'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {running && (
                <span className="inline-block h-5 w-5 animate-spin rounded-full border-b-2 border-brand-600" />
              )}
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={running !== false}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={handleDryRun}
                loading={running === 'dry'}
                disabled={running !== false || !canonicalId}
              >
                Dry run
              </Button>
              <Button
                variant="primary"
                onClick={() => setConfirmOpen(true)}
                disabled={!canMerge}
              >
                Merge
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal (stacks on top — Modal handles z-index layering). */}
      {confirmOpen && (
        <Modal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Confirm merge"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              This will <strong>delete {duplicateIds.length}</strong> duplicate
              doc{duplicateIds.length === 1 ? '' : 's'} and rewrite{' '}
              <strong>{pointerTotal}</strong> downstream pointer
              {pointerTotal === 1 ? '' : 's'}. Continue?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleMergeConfirmed}>
                Delete &amp; merge
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

interface DiffTableProps {
  cluster: DuplicateCluster;
  docs: Record<string, WatchReference | null>;
  canonicalId: string;
  onCanonical: (id: string) => void;
  fieldPicks: Record<string, string>;
  onFieldPick: (path: string, referenceId: string) => void;
}

function DiffTable({
  cluster,
  docs,
  canonicalId,
  onCanonical,
  fieldPicks,
  onFieldPick,
}: DiffTableProps) {
  const docIds = cluster.docs.map((d) => d.referenceId);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Field
            </th>
            {cluster.docs.map((d) => {
              const isCanonical = d.referenceId === canonicalId;
              return (
                <th
                  key={d.referenceId}
                  className={`px-3 py-2 text-left align-top ${
                    isCanonical ? 'bg-brand-50' : ''
                  }`}
                >
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="radio"
                      name="canonical"
                      checked={isCanonical}
                      onChange={() => onCanonical(d.referenceId)}
                      className="mt-0.5 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="min-w-0">
                      <span className="block font-mono text-[11px] text-gray-500">
                        {d.referenceId}
                      </span>
                      <span className="block font-medium text-gray-900">
                        {d.reference || '—'}
                      </span>
                      <span className="mt-1 inline-flex flex-wrap gap-1">
                        <Badge variant="default">score {d.score}</Badge>
                        {isCanonical && (
                          <Badge variant="success">canonical</Badge>
                        )}
                        {d.divergence.isLegacyShape && (
                          <Badge variant="warning">legacy</Badge>
                        )}
                      </span>
                    </span>
                  </label>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {FIELD_GROUPS.map((group) => (
            <FieldGroupRows
              key={group.title}
              group={group}
              docIds={docIds}
              docs={docs}
              canonicalId={canonicalId}
              fieldPicks={fieldPicks}
              onFieldPick={onFieldPick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface FieldGroupRowsProps {
  group: FieldGroup;
  docIds: string[];
  docs: Record<string, WatchReference | null>;
  canonicalId: string;
  fieldPicks: Record<string, string>;
  onFieldPick: (path: string, referenceId: string) => void;
}

function FieldGroupRows({
  group,
  docIds,
  docs,
  canonicalId,
  fieldPicks,
  onFieldPick,
}: FieldGroupRowsProps) {
  return (
    <>
      <tr className="bg-gray-50">
        <td
          colSpan={docIds.length + 1}
          className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400"
        >
          {group.title}
        </td>
      </tr>
      {group.fields.map((field) => {
        const values = docIds.map((id) => ({
          id,
          value: getPath(docs[id] as Record<string, unknown> | null, field.path),
        }));
        const keys = values.map((v) => compareKey(field.path, v.value));
        const allAgree = keys.every((k) => k === keys[0]);
        // `images` is union-merged server-side — show counts, no override radio.
        const selectable = !allAgree && field.path !== 'images';
        // Pre-selection reflects the backend smart-merge winner, not the raw
        // canonical value: when canonical is empty the merge fills from the
        // first non-empty doc, so the radio defaults there too.
        const smartDefault = selectable
          ? smartMergeSource(values, canonicalId)
          : canonicalId;
        const userPicked = field.path in fieldPicks;
        const effectiveSource = userPicked ? fieldPicks[field.path] : smartDefault;
        // Badge only when the auto-default (no user pick) lands on a non-canonical
        // doc — i.e. the merge would fill an empty canonical slot from a duplicate.
        const filledFromSource =
          !userPicked && smartDefault !== canonicalId ? smartDefault : null;

        return (
          <tr key={field.path} className={selectable ? 'bg-yellow-50' : ''}>
            <td className="px-3 py-2 align-top text-gray-700">
              {field.label}
              {selectable && (
                <span className="ml-1 text-xs text-yellow-600">(differs)</span>
              )}
            </td>
            {values.map(({ id, value }) => {
              const isSelected = effectiveSource === id;
              return (
                <td
                  key={id}
                  className={`px-3 py-2 align-top ${
                    id === canonicalId ? 'bg-brand-50/40' : ''
                  }`}
                >
                  <label
                    className={`flex items-start gap-2 ${
                      selectable ? 'cursor-pointer' : ''
                    }`}
                  >
                    {selectable && (
                      <input
                        type="radio"
                        name={`field-${field.path}`}
                        checked={isSelected}
                        onChange={() => onFieldPick(field.path, id)}
                        className="mt-0.5 text-brand-600 focus:ring-brand-500"
                      />
                    )}
                    <span className="min-w-0">
                      <span
                        className={`block break-words font-mono text-xs ${
                          isEmpty(value) ? 'text-gray-300' : 'text-gray-800'
                        } ${selectable && isSelected ? 'font-semibold' : ''}`}
                      >
                        {formatValue(field.path, value)}
                      </span>
                      {filledFromSource === id && (
                        <span
                          className="mt-0.5 block text-[10px] italic text-gray-400"
                          title="Canonical is empty here — the merge would fill this from the duplicate. Pick another radio to override."
                        >
                          → filled from {id.slice(0, 8)}
                        </span>
                      )}
                    </span>
                  </label>
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

function DryRunSummary({
  result,
}: {
  result: MergeReferenceDuplicatesResult;
}) {
  const { rewrites, errors } = result;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <h4 className="mb-1 text-sm font-semibold text-blue-900">
        Dry-run plan
      </h4>
      <p className="text-sm text-blue-800">
        Would delete{' '}
        <span className="font-semibold">{result.duplicatesDeleted}</span>{' '}
        duplicate{result.duplicatesDeleted === 1 ? '' : 's'} and rewrite:{' '}
        {rewrites.bookmarks} bookmarks, {rewrites.wishlists} wishlists,{' '}
        {rewrites.curatedCollections} curated collections,{' '}
        {rewrites.submissionsIndex} submissions-index entries.
      </p>
      {errors.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-semibold text-red-700">
            {errors.length} pre-detected issue
            {errors.length === 1 ? '' : 's'}:
          </p>
          <ul className="mt-1 list-inside list-disc text-xs text-red-700">
            {errors.map((e, i) => (
              <li key={i}>
                <span className="font-medium">{e.surface}:</span> {e.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
